"use client"

import { ApexOptions } from "apexcharts"
import { colors } from "ui/theme/theme"

/**
 * Creates and returns the chart options for the investment simulator
 *
 * @returns ApexOptions object with chart configuration
 */
export const getInvestmentChartOptions = (): ApexOptions => {
  return {
    chart: {
      zoom: {
        enabled: false,
      },
      type: "area",
      height: 158,
      toolbar: {
        show: false,
      },
      fontFamily: "'Ploni DL 1.1 AAA', sans-serif",
      animations: {
        enabled: true,
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      // Improved smoothness settings
      offsetX: 0,
      background: "transparent",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 1.4, // Slightly thicker line for better visibility
      colors: [colors.blue],
      lineCap: "round",
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.3,
        stops: [0, 90, 100],
        colorStops: [
          {
            offset: 0,
            color: colors.blue,
            opacity: 0.4,
          },
          {
            offset: 100,
            color: colors.white,
            opacity: 0.1,
          },
        ],
      },
    },
    xaxis: {
      type: "datetime",
      labels: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      show: false,
      forceNiceScale: true,
      logarithmic: false,
      labels: {
        formatter: (value: number) => `${value.toFixed(0)}`,
      },
      tickAmount: 4, // This ensures we have exactly 4 horizontal grid lines
    },
    tooltip: {
      enabled: false,
    },
    markers: {
      size: 0,
      hover: {
        size: 5,
        sizeOffset: 3,
      },
    },
    grid: {
      show: true,
      borderColor: "rgba(0,0,0,0.1)",
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      row: {
        colors: undefined,
        opacity: 0.1,
      },
      column: {
        colors: undefined,
        opacity: 0.1,
      },
      padding: {
        top: 10,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    colors: [colors.red],
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 240,
          },
          grid: {
            yaxis: {
              lines: {
                show: true,
              },
            },
          },
        },
      },
    ],
  }
}

/**
 * Process data for chart display
 *
 * @param seriesData Initial data points
 * @param timeFrame Selected time frame
 * @returns Processed data points
 */
export const processChartData = (
  seriesData: { x: Date; y: number }[],
  timeFrame: "12m" | "3y" | "5y",
) => {
  // Apply data smoothing and reduction based on time frame
  const dataPointCount = seriesData.length

  // Determine optimal number of points based on time frame
  let targetPoints = 100 // Default target
  if (timeFrame === "12m") {
    targetPoints = 50
  } else if (timeFrame === "3y") {
    targetPoints = 75
  } else if (timeFrame === "5y") {
    targetPoints = 100
  }

  let processedData = [...seriesData]

  // Smooth the data if there are many points
  if (dataPointCount > targetPoints * 2) {
    // First smooth data with moving average
    processedData = smoothData(processedData, 5)

    // Then reduce number of points if still too many
    processedData = reduceDataPoints(processedData, targetPoints)
  }

  return processedData
}

/**
 * Filter data based on time frame
 *
 * @param allData All data points
 * @param timeFrame Selected time frame
 * @returns Filtered data points
 */
export const filterDataByTimeFrame = (
  allData: Array<{ dateObj: Date; value: number }>,
  timeFrame: "12m" | "3y" | "5y",
) => {
  // Filter data based on time frame
  const latestDate = allData[allData.length - 1].dateObj
  let periodStart: Date

  if (timeFrame === "12m") {
    // 12 months back from latest date
    periodStart = new Date(latestDate)
    periodStart.setFullYear(periodStart.getFullYear() - 1)
  } else if (timeFrame === "3y") {
    // 3 years back from latest date
    periodStart = new Date(latestDate)
    periodStart.setFullYear(periodStart.getFullYear() - 3)
  } else {
    // 5 years back from latest date
    periodStart = new Date(latestDate)
    periodStart.setFullYear(periodStart.getFullYear() - 5)
  }

  const periodData = allData.filter((item) => item.dateObj >= periodStart)
  return periodData.length > 0 ? periodData : allData
}

// Import and re-export data processing functions from utils
import { reduceDataPoints, smoothData } from "./utils"
export { reduceDataPoints, smoothData }
